/**
 * シフト提出(2026-09-12 新設)。本人が「その月のどの日に何時から何時まで入れるか」を出す。
 *
 * 1人1か月 = `shift_submissions/{month}_{op_id}`。日ごとに 開始・終了(0.5h刻み)。休みの日は持たない。
 * 管理者はこれを見て **出勤表(`shifts/{date}`)** を作る。
 *  - 提出シフト時間 = ここの合計(現行「オペレーター数字確認シート」の「提出シフト時間」)
 *  - 修正シフト時間 = 出勤表の稼働の合計(管理者が直した後の値)
 */
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where, documentId, orderBy } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import type { MonthStr } from '@core/lib/types';

/**
 * 日(1〜31) → その日の希望。入れない日はキーを持たない。
 * `bb_id` は出勤形態×報酬区分。**1日に入れられるのは1つだけ**(品川と在宅を同じ日に出せない。2026-09-12 指示)
 */
export type SubmitDay = {
  bb_id?: string;
  start: number;
  end: number;
  /** 欠勤・当日欠勤。入っている日は稼働時間に数えない(2026-09-12) */
  status?: 'absent' | 'same_day';
};

/** 変更の記録(誰がいつ、どの日を、どう変えたか) */
export type ShiftChangeLog = { day: string; before: string; after: string; by: string; at: string };

export type ShiftSubmission = {
  id: string; // = `${month}_${op_id}`
  month: MonthStr;
  op_id: string;
  name: string;
  /** 出勤形態×報酬区分。複数持っている人はどれか1つだけ選ぶ(2026-09-12 指示) */
  bb_id: string;
  days: Record<string, SubmitDay>;
  note: string;
  /** 提出後の変更履歴(新しい順) */
  logs?: ShiftChangeLog[];
  submitted_at?: unknown;
  submitted_by?: string;
};

export const submissionId = (month: MonthStr, opId: string) => `${month}_${opId}`;

/** 提出時間の合計(h) */
export function dayHours(d: SubmitDay | undefined): number {
  if (!d || d.status) return 0;
  return Math.max(0, Math.round((d.end - d.start) * 2) / 2);
}

export function submittedHours(days: Record<string, SubmitDay>): number {
  let h = 0;
  for (const d of Object.values(days)) h += dayHours(d);
  return Math.round(h * 2) / 2;
}

function normalize(days: Record<string, SubmitDay>): Record<string, SubmitDay> {
  const out: Record<string, SubmitDay> = {};
  for (const [k, v] of Object.entries(days ?? {})) {
    if (!v || typeof v.start !== 'number' || typeof v.end !== 'number') continue;
    const start = Math.round(v.start * 2) / 2;
    const end = Math.round(v.end * 2) / 2;
    if (end <= start) continue;
    const base: SubmitDay = { start, end };
    if (v.bb_id) base.bb_id = v.bb_id;
    if (v.status === 'absent' || v.status === 'same_day') base.status = v.status;
    out[String(Number(k)).padStart(2, '0')] = base;
  }
  return out;
}

export async function fetchSubmission(month: MonthStr, opId: string): Promise<ShiftSubmission | null> {
  const snap = await getDoc(doc(getDb(), 'shift_submissions', submissionId(month, opId)));
  if (!snap.exists()) return null;
  const d = snap.data() as Omit<ShiftSubmission, 'id'>;
  return { id: snap.id, ...d, days: normalize(d.days) };
}

/** その月の全員分 */
export async function fetchSubmissions(month: MonthStr): Promise<ShiftSubmission[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'shift_submissions'), where(documentId(), '>=', `${month}_`), where(documentId(), '<=', `${month}_`), orderBy(documentId())),
  );
  return snap.docs.map((d) => {
    const v = d.data() as Omit<ShiftSubmission, 'id'>;
    return { id: d.id, ...v, days: normalize(v.days) };
  });
}

export async function saveSubmission(args: {
  month: MonthStr;
  opId: string;
  name: string;
  bbId: string;
  days: Record<string, SubmitDay>;
  note: string;
  userId: string;
  logs?: ShiftChangeLog[];
}): Promise<void> {
  const { month, opId, name, bbId, days, note, userId, logs } = args;
  await setDoc(doc(getDb(), 'shift_submissions', submissionId(month, opId)), {
    month,
    op_id: opId,
    name,
    bb_id: bbId,
    days: normalize(days),
    note: (note ?? '').trim(),
    logs: (logs ?? []).slice(0, 200),
    submitted_at: serverTimestamp(),
    submitted_by: userId,
  });
}
